import { useCallback, useMemo, useState } from 'react';
import { useGetUserBookmarks } from '@/lib/bookmark/hooks/use-get-user-bookmarks';
import type {
  UserBookmarks,
  WorkEngagementBookmarks,
} from '@/lib/bookmark/types';
import { useDeepCompareEffect } from '@/hooks/use-deep-compare-effect';
import { useGetWorkEngagementBookmarks } from '@/lib/bookmark/hooks/use-get-work-engagement-bookmarks';
import { useFetchAllInstances } from '@/requests/hooks/use-fetch-all-instances';
import {
  OrganizationUserWorkEngagementConfig,
  type OrganizationUserWorkEngagement,
} from '@pigello/pigello-matrix';
import { useSession } from '@/providers/session-provider';
import { useSaveUserBookmarks } from '@/lib/bookmark/hooks/use-handle-user-bookmarks';
import { useSaveWorkEngagementBookmarks } from '@/lib/bookmark/hooks/use-handle-work-engagement-bookmarks';

interface UseWorkflowsDataProps {
  workEngagementIds?: string[];
}

export function useWorkflowsData({
  workEngagementIds,
}: UseWorkflowsDataProps = {}) {
  const {
    data: {
      session: { userId },
    },
  } = useSession();

  const { data: userWorkflows, isLoading: isLoadingUserWorkflows } =
    useGetUserBookmarks();

  const [localStateUserWorkflows, setLocalStateUserWorkflows] =
    useState<UserBookmarks>();

  // Sync local state with server data to prevent DND flickering
  useDeepCompareEffect(() => {
    setLocalStateUserWorkflows(userWorkflows);
  }, [userWorkflows]);

  const { data: workEngagements, isLoading: isLoadingWorkEngagements } =
    useFetchAllInstances<OrganizationUserWorkEngagement>({
      modelName: OrganizationUserWorkEngagementConfig.modelName,
      filters:
        workEngagementIds && workEngagementIds.length > 0
          ? { id: { __in: workEngagementIds } }
          : { user: { noop: userId } },
      enabled: !!userId,
      nested: ['workEngagement'],
      enableGlobalFilters: true,
    });

  const computedWorkEngagementIds = useMemo(() => {
    if (workEngagementIds && workEngagementIds.length > 0)
      return workEngagementIds;
    return [
      ...new Set(
        (workEngagements?.list ?? [])
          .map((w) => w.workEngagement?.id)
          .filter((id): id is string => id != null)
      ),
    ];
  }, [workEngagements, workEngagementIds]);

  const {
    data: workEngagementWorkflows,
    isLoading: isLoadingWorkEngagementWorkflows,
  } = useGetWorkEngagementBookmarks(computedWorkEngagementIds, {
    enabled: computedWorkEngagementIds.length > 0,
  });

  const [
    localStateWorkEngagementWorkflows,
    setLocalStateWorkEngagementWorkflows,
  ] = useState<WorkEngagementBookmarks[]>();

  // Sync local state with server data to prevent DND flickering
  useDeepCompareEffect(() => {
    setLocalStateWorkEngagementWorkflows(workEngagementWorkflows);
  }, [workEngagementWorkflows]);

  const sharedFolders = useMemo(() => {
    return userWorkflows?.folders_shared_with_me.filter(
      (folder) => folder.kind === 'workflow'
    );
  }, [userWorkflows]);

  const { mutateAsync: saveUserBookmarks, isPending: isSavingUserBookmarks } =
    useSaveUserBookmarks();
  const {
    mutateAsync: saveWorkEngagementBookmarks,
    isPending: isSavingWorkEngagementBookmarks,
  } = useSaveWorkEngagementBookmarks(
    workEngagementIds && workEngagementIds.length > 0
      ? workEngagementIds[0]
      : null
  );
  const save = async (data: UserBookmarks | WorkEngagementBookmarks) => {
    if (workEngagementIds && workEngagementIds.length > 0) {
      return saveWorkEngagementBookmarks(data);
    }
    return saveUserBookmarks(data as UserBookmarks);
  };

  const isSaving =
    workEngagementIds && workEngagementIds.length > 0
      ? isSavingWorkEngagementBookmarks
      : isSavingUserBookmarks;

  const workflows = useMemo(() => {
    if (workEngagementIds?.length) {
      return localStateWorkEngagementWorkflows?.[0];
    }

    return localStateUserWorkflows;
  }, [
    workEngagementIds,
    localStateUserWorkflows,
    localStateWorkEngagementWorkflows,
  ]);

  const setWorkflows = useCallback(
    (workflows: UserBookmarks | WorkEngagementBookmarks) => {
      if (workEngagementIds?.length) {
        setLocalStateWorkEngagementWorkflows([
          workflows as WorkEngagementBookmarks,
        ]);
      } else {
        setLocalStateUserWorkflows(workflows as UserBookmarks);
      }
    },
    [workEngagementIds]
  );

  return {
    userWorkflows: localStateUserWorkflows,
    isLoadingUserWorkflows: isLoadingUserWorkflows,
    workEngagements,
    workEngagementWorkflows: localStateWorkEngagementWorkflows,
    isLoadingWorkEngagementWorkflows:
      isLoadingWorkEngagements || isLoadingWorkEngagementWorkflows,
    sharedFolders,
    workflows,
    setWorkflows,
    save,
    isSaving,
  };
}
